/**
 * ReplyPreview - Preview da mensagem sendo respondida.
 * Aparece acima do input quando o usuário está respondendo uma mensagem.
 */
import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ChatMessage } from '../../hooks/types';
import { formatMessageText } from '../../../../utils/sanitize';
import { ensureSignatureLineBreak } from '../../../../utils/messageSignature';
import { isTemporaryRemoteMediaUrl } from '../../../../utils/failedImageCache';
import { chatMediaIconName } from './media/MediaFallback';

interface ReplyPreviewProps {
    message: ChatMessage;
    conversationName: string;
    onCancel: () => void;
}

export const ReplyPreview: React.FC<ReplyPreviewProps> = ({ message, conversationName, onCancel }) => {
    const senderName = message.sender === 'me' ? 'Você' : conversationName;
    const hasMedia = message.type !== 'text';
    const [thumbFailed, setThumbFailed] = React.useState(false);

    React.useEffect(() => {
        setThumbFailed(false);
    }, [message.id, message.media?.url]);

    const showReplyThumb =
        message.type === 'image' &&
        !!message.media?.url &&
        !thumbFailed &&
        !isTemporaryRemoteMediaUrl(message.media.url);

    return (
        <div
            className="flex items-stretch gap-0 border-t border-border bg-muted/40 px-3 py-2"
            role="status"
            aria-live="polite"
        >
            <div className="w-1 shrink-0 self-stretch rounded-full bg-primary" aria-hidden="true" />

            <div className="flex min-w-0 flex-1 items-center gap-2 py-0.5 pl-2.5">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-primary">
                        Respondendo a {senderName}
                    </p>
                    <p className="mt-0.5 flex min-w-0 items-start gap-1 text-sm text-muted-foreground">
                        {hasMedia && chatMediaIconName(message.type) ? (
                            <AppIcon
                                name={chatMediaIconName(message.type)!}
                                className="mt-0.5 size-3.5 shrink-0 opacity-70"
                                aria-hidden="true"
                            />
                        ) : null}
                        <span
                            className="min-w-0 whitespace-pre-line font-sans line-clamp-2"
                            dangerouslySetInnerHTML={{
                                __html: formatMessageText(
                                    ensureSignatureLineBreak(message.text || message.type),
                                ),
                            }}
                        />
                    </p>
                </div>

                {showReplyThumb ? (
                    <img
                        src={message.media!.url}
                        alt=""
                        className="size-11 shrink-0 rounded-sm object-cover pointer-fine:size-10"
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        onError={() => setThumbFailed(true)}
                    />
                ) : null}

                <button
                    type="button"
                    onClick={onCancel}
                    className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground pointer-fine:size-9"
                    title="Cancelar resposta"
                    aria-label="Cancelar resposta"
                >
                    <AppIcon name="x" className="size-5" />
                </button>
            </div>
        </div>
    );
};
